import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Truck,
  Edit,
  Trash2,
  Eye,
  Search,
  RefreshCw,
  AlertTriangle,
  X,
  Save,
  AlertCircle,
} from "lucide-react";
import Swal from "sweetalert2";

const ListarVehiculos = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [vehiculos, setVehiculos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // "view" | "edit"
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const API_LARAVEL_URL =
    process.env.REACT_APP_API_URL || "http://localhost:8000/api";

  const getApiHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  useEffect(() => {
    if (user?.empresa_id) {
      loadVehiculos();
    }
  }, [user]);

  const loadVehiculos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_LARAVEL_URL}/flota/vehiculos`, {
        headers: getApiHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setVehiculos(data);
      }
    } catch (error) {
      console.error("Error cargando vehículos:", error);
      Swal.fire("Error", "No se pudieron cargar los vehículos", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = (vehiculo) => {
    setSelectedVehiculo(vehiculo);
    setFormData(vehiculo);
    setModalMode("view");
    setShowModal(true);
  };

  const handleEdit = (vehiculo) => {
    setSelectedVehiculo(vehiculo);
    setFormData(vehiculo);
    setModalMode("edit");
    setShowModal(true);
    setErrors({});
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedVehiculo(null);
    setFormData({});
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_LARAVEL_URL}/flota/vehiculos/${selectedVehiculo.id}`,
        {
          method: "PUT",
          headers: getApiHeaders(),
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        Swal.fire({
          icon: "success",
          title: "¡Actualizado!",
          text: data.message || "Vehículo actualizado correctamente",
        });
        handleCloseModal();
        loadVehiculos();
      } else {
        if (data.errors) {
          setErrors(data.errors);
        } else {
          throw new Error(data.message || "Error al actualizar");
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo actualizar el vehículo",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (vehiculo) => {
    const result = await Swal.fire({
      title: "¿Confirmar eliminación?",
      text: `¿Deseas eliminar el vehículo ${vehiculo.placa}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          `${API_LARAVEL_URL}/flota/vehiculos/${vehiculo.id}`,
          {
            method: "DELETE",
            headers: getApiHeaders(),
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          Swal.fire(
            "Eliminado",
            data.message || "Vehículo eliminado correctamente",
            "success"
          );
          loadVehiculos();
        } else {
          throw new Error(data.message || "Error al eliminar");
        }
      } catch (error) {
        Swal.fire(
          "Error",
          error.message || "No se pudo eliminar el vehículo",
          "error"
        );
      }
    }
  };

  const filteredVehiculos = vehiculos.filter(
    (v) =>
      (v.placa || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (v.marca || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (v.modelo || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 mb-4">
        <div className="px-4 py-3">
          <div className="flex items-center">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-2 rounded-lg mr-3">
              <Truck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                Lista de Vehículos
              </h1>
              <p className="text-sm text-gray-600">
                Gestiona los vehículos de tu flota
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4">
        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar por placa, marca o modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <button
              onClick={loadVehiculos}
              className="px-4 py-2 text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg font-medium transition-colors text-sm flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Recargar
            </button>
          </div>
        </div>

        {/* Tabla */}
        {loading && !showModal ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando vehículos...</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">
                      Placa
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">
                      Marca / Modelo
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">
                      IMEI
                    </th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredVehiculos.length > 0 ? (
                    filteredVehiculos.map((vehiculo) => (
                      <tr
                        key={vehiculo.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <Truck className="h-4 w-4 text-blue-500 mr-2" />
                            <span className="font-medium text-gray-900">
                              {vehiculo.placa}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {vehiculo.marca || "-"} {vehiculo.modelo || ""}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {vehiculo.tipo_vehiculo || "-"}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">
                          {vehiculo.imei || "-"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              (vehiculo.estado || "").toUpperCase() === "ACTIVO"
                                ? "bg-green-100 text-green-800"
                                : (vehiculo.estado || "").toUpperCase() === "MANTENIMIENTO"
                                ? "bg-amber-100 text-amber-800"
                                : (vehiculo.estado || "").toUpperCase() === "REPARACION"
                                ? "bg-orange-100 text-orange-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {vehiculo.estado === "ACTIVO"
                              ? "Activo"
                              : vehiculo.estado === "MANTENIMIENTO"
                              ? "Mantenimiento"
                              : vehiculo.estado === "REPARACION"
                              ? "Reparación"
                              : vehiculo.estado === "INACTIVO"
                              ? "Inactivo"
                              : vehiculo.estado || "Inactivo"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => handleView(vehiculo)}
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                              title="Ver detalles"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(vehiculo)}
                              className="p-1 text-emerald-600 hover:bg-emerald-100 rounded transition-colors"
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(vehiculo)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center">
                        <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500">
                          {searchTerm
                            ? "No se encontraron vehículos"
                            : "No hay vehículos registrados"}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal Ver/Editar */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div
              className={`px-6 py-4 ${modalMode === "view"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-600"
                  : "bg-gradient-to-r from-emerald-500 to-teal-600"
                }`}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">
                  {modalMode === "view"
                    ? "Detalles del Vehículo"
                    : "Editar Vehículo"}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Placa */}
                <div>
                  <label htmlFor="placa_modal" className="block text-sm font-medium text-gray-700 mb-1">
                    Placa
                  </label>
                  {modalMode === "view" ? (
                    <p className="text-gray-900 font-medium">
                      {formData.placa}
                    </p>
                  ) : (
                    <>
                      <input
                        id="placa_modal"
                        type="text"
                        name="placa"
                        value={formData.placa || ""}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${errors.placa ? "border-red-500" : "border-gray-300"
                          }`}
                      />
                      {errors.placa && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {errors.placa[0]}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Tipo */}
                <div>
                  <label htmlFor="tipo_vehiculo_modal" className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Vehículo
                  </label>
                  {modalMode === "view" ? (
                    <p className="text-gray-900">{formData.tipo_vehiculo}</p>
                  ) : (
                    <select
                      id="tipo_vehiculo_modal"
                      name="tipo_vehiculo"
                      value={formData.tipo_vehiculo || "CAMION"}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="CAMION">Camión</option>
                      <option value="CARRETA">Carreta</option>
                      <option value="VAN">Van</option>
                      <option value="OTROS">Otros</option>
                    </select>
                  )}
                </div>

                {/* Marca */}
                <div>
                  <label htmlFor="marca_modal" className="block text-sm font-medium text-gray-700 mb-1">
                    Marca
                  </label>
                  {modalMode === "view" ? (
                    <p className="text-gray-900">{formData.marca || "-"}</p>
                  ) : (
                    <input
                      id="marca_modal"
                      type="text"
                      name="marca"
                      value={formData.marca || ""}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  )}
                </div>

                {/* Modelo */}
                <div>
                  <label htmlFor="modelo_modal" className="block text-sm font-medium text-gray-700 mb-1">
                    Modelo
                  </label>
                  {modalMode === "view" ? (
                    <p className="text-gray-900">{formData.modelo || "-"}</p>
                  ) : (
                    <input
                      id="modelo_modal"
                      type="text"
                      name="modelo"
                      value={formData.modelo || ""}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  )}
                </div>

                {/* Año */}
                <div>
                  <label htmlFor="año_modal" className="block text-sm font-medium text-gray-700 mb-1">
                    Año
                  </label>
                  {modalMode === "view" ? (
                    <p className="text-gray-900">{formData.año || "-"}</p>
                  ) : (
                    <input
                      id="año_modal"
                      type="number"
                      name="año"
                      value={formData.año || ""}
                      onChange={handleChange}
                      min="1900"
                      max={new Date().getFullYear() + 1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  )}
                </div>

                {/* Capacidad */}
                <div>
                  <label htmlFor="capacidad_toneladas_modal" className="block text-sm font-medium text-gray-700 mb-1">
                    Capacidad (Tons)
                  </label>
                  {modalMode === "view" ? (
                    <p className="text-gray-900">
                      {formData.capacidad_toneladas || "-"}
                    </p>
                  ) : (
                    <input
                      id="capacidad_toneladas_modal"
                      type="number"
                      name="capacidad_toneladas"
                      value={formData.capacidad_toneladas || ""}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  )}
                </div>

                {/* IMEI */}
                <div>
                  <label htmlFor="imei_modal" className="block text-sm font-medium text-gray-700 mb-1">
                    IMEI (GPS)
                  </label>
                  {modalMode === "view" ? (
                    <p className="text-gray-900 text-xs">
                      {formData.imei || "-"}
                    </p>
                  ) : (
                    <input
                      id="imei_modal"
                      type="text"
                      name="imei"
                      value={formData.imei || ""}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  )}
                </div>

                {/* Estado */}
                <div>
                  <label htmlFor="estado_modal" className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  {modalMode === "view" ? (
                    <p className="text-gray-900">{formData.estado}</p>
                  ) : (
                    <select
                      id="estado_modal"
                      name="estado"
                      value={formData.estado || "ACTIVO"}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="ACTIVO">Activo</option>
                      <option value="INACTIVO">Inactivo</option>
                      <option value="MANTENIMIENTO">Mantenimiento</option>
                    </select>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                {modalMode === "view" ? "Cerrar" : "Cancelar"}
              </button>
              {modalMode === "edit" && (
                <button
                  onClick={handleSaveEdit}
                  disabled={loading}
                  className="px-6 py-2 text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 rounded-lg font-medium transition-all duration-200 flex items-center shadow-lg hover:shadow-xl disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListarVehiculos;
